/**
 * TemplatePicker
 *
 * Selector de plantillas WhatsApp aprobadas en el paso 2 del wizard.
 * Igual que SegmentPicker, ofrece dos modos:
 *
 *   1. Elegir una existente — lista de plantillas approved
 *   2. Crear una nueva aquí — abre InlineTemplateCreator (sin salir
 *      del wizard, sin perder contexto)
 *
 * Esta versión NO tiene links a /whatsapp/templates ni a otras pantallas:
 * todo se resuelve dentro del modal para que el usuario no pierda dónde
 * estaba en el wizard.
 */

import { useEffect, useMemo, useState } from "react";
import {
    FileText,
    Search,
    Check,
    CheckCircle2,
    Loader2,
    AlertCircle,
    Plus,
    Sparkles,
    Pencil,
} from "lucide-react";
import { whatsappApi } from "../../whatsapp/api";
import type { WhatsAppTemplate } from "../../whatsapp/types";
import { InlineTemplateCreator } from "./InlineTemplateCreator";

interface TemplatePickerProps {
    brandId: string;
    selectedId?: string;
    onSelect: (template: WhatsAppTemplate) => void;
}

type Mode = "pick" | "create";

export function TemplatePicker({
    brandId,
    selectedId,
    onSelect,
}: TemplatePickerProps) {
    const [mode, setMode] = useState<Mode>("pick");
    const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [creatorOpen, setCreatorOpen] = useState(false);

    const loadTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await whatsappApi.getTemplates(brandId);
            if (res.success) {
                setTemplates(
                    res.data.filter((t) => t.approvalStatus === "approved"),
                );
            } else {
                setError(res.error || "Error al cargar plantillas");
            }
        } catch (err: any) {
            setError(err.message || "Error inesperado");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter(
            (t) =>
                t.name.toLowerCase().includes(q) ||
                t.body.toLowerCase().includes(q),
        );
    }, [templates, search]);

    const handleCreated = async (template: WhatsAppTemplate) => {
        setCreatorOpen(false);
        await loadTemplates();
        onSelect(template);
        setMode("pick");
    };

    return (
        <>
            {/* Mode toggle */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                <ModeCard
                    icon={FileText}
                    label="Elegir una existente"
                    description={`${templates.length} plantilla${templates.length === 1 ? "" : "s"} aprobada${templates.length === 1 ? "" : "s"}`}
                    active={mode === "pick"}
                    accent="emerald"
                    onClick={() => setMode("pick")}
                />
                <ModeCard
                    icon={Sparkles}
                    label="Crear una nueva"
                    description="Describí qué querés decir y la IA arma el mensaje"
                    active={mode === "create"}
                    accent="violet"
                    onClick={() => {
                        setMode("create");
                        setCreatorOpen(true);
                    }}
                />
            </div>

            {/* PICK MODE */}
            {mode === "pick" && (
                <>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 bg-slate-50 rounded-2xl">
                            <Loader2
                                className="animate-spin text-violet-500 mb-3"
                                size={28}
                            />
                            <p className="text-sm text-slate-500">
                                Cargando plantillas…
                            </p>
                        </div>
                    ) : error ? (
                        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                            <AlertCircle size={18} />
                            <span className="text-sm font-medium">{error}</span>
                        </div>
                    ) : templates.length === 0 ? (
                        <EmptyState onCreate={() => setCreatorOpen(true)} />
                    ) : (
                        <div className="space-y-3">
                            <div className="relative">
                                <Search
                                    size={15}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                                />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar por nombre o contenido…"
                                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                                        focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                                />
                            </div>

                            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                                {filtered.length === 0 ? (
                                    <div className="text-sm text-slate-400 py-4 text-center">
                                        Ninguna plantilla coincide con "{search}"
                                    </div>
                                ) : (
                                    filtered.map((tpl) => {
                                        const isSelected = selectedId === tpl.id;
                                        return (
                                            <button
                                                key={tpl.id}
                                                onClick={() => onSelect(tpl)}
                                                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                                                    isSelected
                                                        ? "border-emerald-300 bg-emerald-50"
                                                        : "border-slate-100 bg-white hover:border-slate-200"
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <CheckCircle2
                                                        size={16}
                                                        className="text-emerald-500 flex-shrink-0 mt-0.5"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <p className="font-mono text-sm font-semibold text-slate-900 truncate">
                                                                {tpl.name}
                                                            </p>
                                                            {isSelected && (
                                                                <Check
                                                                    size={14}
                                                                    className="text-emerald-600 flex-shrink-0"
                                                                />
                                                            )}
                                                            <span className="ml-auto text-[10px] text-slate-400 font-mono">
                                                                {tpl.language}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed whitespace-pre-wrap">
                                                            {tpl.body}
                                                        </p>
                                                        <p className="text-[10px] text-slate-400 mt-1.5">
                                                            {tpl.variableCount}{" "}
                                                            {tpl.variableCount === 1
                                                                ? "variable"
                                                                : "variables"}{" "}
                                                            · $
                                                            {tpl.estimatedCostUSD.toFixed(
                                                                4,
                                                            )}{" "}
                                                            / conversación
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>

                            {/* Bottom CTA "Crear otra" siempre disponible */}
                            <button
                                type="button"
                                onClick={() => setCreatorOpen(true)}
                                className="w-full mt-2 py-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 text-slate-500 hover:text-emerald-700 text-sm font-medium transition-all flex items-center justify-center gap-2"
                            >
                                <Plus size={14} />
                                Crear una plantilla nueva
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* CREATE MODE — quien cerró el modal sin crear */}
            {mode === "create" && !creatorOpen && (
                <div className="bg-emerald-50/40 border border-emerald-100 rounded-2xl p-6 text-center">
                    <Sparkles size={28} className="text-emerald-500 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-slate-900 mb-1">
                        ¿Cerraste el creador?
                    </p>
                    <p className="text-xs text-slate-500 mb-4">
                        Podés volver a abrirlo o cambiar a la lista de plantillas
                        existentes.
                    </p>
                    <div className="flex gap-2 justify-center">
                        <button
                            type="button"
                            onClick={() => setCreatorOpen(true)}
                            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                        >
                            <Pencil size={13} />
                            Abrir creador
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("pick")}
                            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                        >
                            Ver existentes
                        </button>
                    </div>
                </div>
            )}

            {/* Modal */}
            <InlineTemplateCreator
                open={creatorOpen}
                brandId={brandId}
                onClose={() => setCreatorOpen(false)}
                onCreated={handleCreated}
            />
        </>
    );
}

/* ─── Mode card ─── */
interface ModeCardProps {
    icon: typeof FileText;
    label: string;
    description: string;
    active: boolean;
    accent: "emerald" | "violet";
    onClick: () => void;
}

function ModeCard({
    icon: Icon,
    label,
    description,
    active,
    accent,
    onClick,
}: ModeCardProps) {
    const activeClasses =
        accent === "emerald"
            ? "border-emerald-300 bg-emerald-50"
            : "border-violet-300 bg-violet-50";
    const iconBg =
        accent === "emerald"
            ? active
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            : active
                ? "bg-violet-100 text-violet-700"
                : "bg-slate-100 text-slate-500";

    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
                active ? activeClasses : "border-slate-100 hover:border-slate-200"
            }`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${iconBg}`}
                >
                    <Icon size={17} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                        {description}
                    </p>
                </div>
            </div>
        </button>
    );
}

/* ─── Empty state ─── */
function EmptyState({ onCreate }: { onCreate: () => void }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
            <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText size={26} className="text-slate-400" />
            </div>
            <p className="text-slate-700 font-medium mb-1">
                Todavía no tenés plantillas aprobadas
            </p>
            <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
                Creá la primera acá mismo en 30 segundos describiendo qué querés
                decir.
            </p>
            <button
                type="button"
                onClick={onCreate}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-sm shadow-emerald-500/25"
            >
                <Sparkles size={14} />
                Crear mi primera plantilla
            </button>
        </div>
    );
}

export default TemplatePicker;
